import type { Option, Timescale } from "@tento-lona/sheets";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaIcons } from "@tento-ui/ui/icons";
import eyeIcon from "@tento-ui/icons/eye.svg";
import eyeSlashedIcon from "@tento-ui/icons/eye-slashed.svg";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import { type SheetRow } from "@tento-lona";
import { Tracing } from "@tento-core/tracing";
import type { SheetLegendClass } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import type { TextMeasurer } from "@tento-lona/sheets-ui/sheet/default-legend-layout.ts";

/** Width reserved for the show/hide toggle beside the label. */
const TOGGLE_WIDTH_PX = 28;

/**
 * The raw-JSON row's hide state, owned by the hosting plugin (persisted as a
 * row attribute). A narrow interface instead of a plugin class keeps this
 * component cycle-free with `column-data-json.ts` and lets the source-chrome
 * plugin host it too.
 */
export interface JsonHideHost {
  /** Whether this row gets the eye at all (e.g. only json-dtype sources). */
  canHide(row: SheetRow.Joined.Handle): boolean;
  isHidden(row: SheetRow.Joined.Handle): boolean;
  setHidden(row: SheetRow.Joined.Handle, hidden: boolean): void;
}

// The hide-flag attribute shape lives in the SDK (`JsonHide` in
// `@tento/lona`) so the Data/DataJson descriptors' cellHeight callbacks can
// collapse a hidden row's lane. Re-exported here for existing importers.
import { JsonHide } from "@tento-lona/internal-ui";
export { JsonHide } from "@tento-lona/internal-ui";

/**
 * Legend for a raw-JSON data row: default label row + an eye toggle that
 * hides/shows the row's JSON cells. The counterpart of the canvas legend's
 * eye (which drives an alias's data-json CHILD row) for concrete source
 * rows that render their own cells.
 */
@component({
  name: "std-data-json-legend",
})
export class DataJsonLegend extends LonaWebComponent {
  #$legend: SheetLegend.DefaultRow = this.hydrate("legend");
  #$iconShow: HTMLElement;
  #$iconHide: HTMLElement;

  hostRef: Option<JsonHideHost> = null;
  #rowRef: Option<SheetRow.Joined.Handle> = null;
  #handleRef: Option<SheetDelegate> = null;
  #dataHidden = false;

  constructor() {
    super();
    this.#$iconShow = LonaIcons.make(eyeIcon);
    this.#$iconHide = LonaIcons.make(eyeSlashedIcon);
    this.#$iconHide.style.display = "none";
    this.$("toggle").appendChild(this.#$iconShow);
    this.$("toggle").appendChild(this.#$iconHide);

    GESTURE_MANAGER.addPointerEvent(this.$("toggle"), {
      onClick: () => {
        const host = this.hostRef;
        const row = this.#rowRef;
        const handle = this.#handleRef;
        if (!host || !row || !handle) {
          // A click that lands here is the invisible failure mode — the eye
          // looks live but has no wiring. Surface it in session traces.
          Tracing.log("data-json-eye/click-unwired", {
            host: host != null,
            row: row?.id.toString() ?? null,
            handle: handle != null,
          });
          return;
        }
        Tracing.log("data-json-eye/click", {
          row: row.id.toString(),
          hiddenNext: !this.#dataHidden,
        });
        this.#dataHidden = !this.#dataHidden;
        host.setHidden(row, this.#dataHidden);
        this.#updateIconState(this.#dataHidden);
        // Immediate feedback: setHidden applies the attribute locally before
        // the PATCH; re-bind the row's cells, then reflow heights — the
        // descriptor's cellHeight callback collapses/restores the lane, and
        // rebindRow alone never recomputes the layout map.
        void handle.rebind.rebindRow(row);
        handle.rebind.invalidateRowHeight();
      },
    });
  }

  #updateIconState(hidden: boolean): void {
    this.#$iconShow.style.display = hidden ? "none" : "";
    this.#$iconHide.style.display = hidden ? "" : "none";
  }

  bind(row: SheetRow.Joined.Handle, handle: SheetDelegate): void {
    this.#rowRef = row;
    this.#handleRef = handle;
    this.#dataHidden = this.hostRef?.isHidden(row) ?? false;
    this.#$legend.bind(
      row,
      (target) => handle.rebind.revealRowOptionsPopover(row, target),
      handle,
    );
    this.#updateIconState(this.#dataHidden);
    this.$("toggle").style.display = this.hostRef?.canHide(row) ? "" : "none";
  }

  static makeFactory(host: JsonHideHost): SheetLegendClass<DataJsonLegend> {
    return class {
      static make(): DataJsonLegend {
        return DataJsonLegend.make();
      }
      static bind(
        $legend: DataJsonLegend,
        row: SheetRow.Joined.Handle,
        handle: SheetDelegate,
        _sheetTimescale: Timescale,
      ): void {
        $legend.hostRef = host;
        $legend.bind(row, handle);
      }
      static computeWidth(
        row: SheetRow.Joined.Handle,
        measure: TextMeasurer,
        sheetTimescale: string,
      ): number {
        return (
          SheetLegend.DefaultFactory.computeWidth(row, measure, sheetTimescale) +
          TOGGLE_WIDTH_PX
        );
      }
    };
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: flex;
        align-items: center;
      }

      #legend {
        flex: 1 1 auto;
        min-width: 0;
      }

      #toggle {
        display: flex;
        align-items: center;
        padding: 4px 6px;
        cursor: pointer;
        opacity: 0.5;
        color: var(--text-sec);
        flex-shrink: 0;
        border-radius: 4px;
      }

      #toggle:hover {
        opacity: 1;
        background: var(--bg-hover);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-sheet-legend-default-row id="legend"></std-sheet-legend-default-row>
    <div id="toggle"></div>
  `;
}
